import React, { useState, useEffect, useContext } from 'react'
import { Layout } from "../../componentsV1/layout"
import css from "./checkout.module.scss"
import { Checkbox } from '../../componentsV1/Checkbox'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../../context/auth'
import { Input } from '../../componentsV1/Input'
import { updateCartApi, getShippingOptionsApi, setShippingMethodsApi, createPaymentSessionsApi, updatePaymentSessionsApi, selectPaymentSessionApi, completeCartApi, addDiscountApi } from '../../api'

import {
    CardNumberElement,
    CardCvcElement,
    CardExpiryElement,
    CardElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import { countryCode } from '../../utils/country'
import { removeLocalStorage } from '../../helpers/localStorage';
import { useGtag } from '../../hooks/useGtag'

export const Checkout = () => {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const { gtagCheckout, gtagBooking } = useGtag()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { setBillingAddressModal, setShippingAddressModal, setSelectedAddress, setCompleteOrderModal, user, setIsLoading, cartData, setCartData, setNotification, setCompletedOrderData } = useContext(AuthContext)
    const [step, setStep] = useState(1)
    const [shippingAddress, setShippingAddress] = useState([])
    const [billingAddress, setBillingAddress] = useState([])
    const [selectShippingAddress, setSelectShippingAddress] = useState(null)
    const [selectBillingAddress, setSelectBillingAddress] = useState(null)
    const [isAddressSame, setIsAddressSame] = useState(false)
    const [paymentType, setPaymentType] = useState('')
    const [creditCardHolderName, setCreditCardHolderName] = useState('')
    const [isAgree, setIsAgree] = useState(false)
    const [quantity, setQuantity] = useState(1)
    const [freeQuantity, setFreeQuantity] = useState(0)
    const [shippingOptions, setShippingOptions] = useState([])
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('')
    const [purchaseOrderReference, setPurchaseOrderReference] = useState('')
    const [contract, setContract] = useState('')
    const [cardDetails, setCardDetails] = useState({
        cardNumber: false,
        cardCvc: false,
        cardExpiry: false,
    })
    const [paymentSession, setPaymentSession] = useState(null)
    const [cardNumberElement, setCardNumberElement] = useState(null)
    const [cardCvcElement, setCardCvcElement] = useState(null)
    const [cardExpiryElement, setCardExpiryElement] = useState(null)


    useEffect(() => {
        if (cartData && cartData?.items?.length > 0) {
            setQuantity(cartData?.items?.reduce((acc, curr) => acc + curr?.quantity, 0))
            setFreeQuantity(cartData?.items?.reduce((acc, curr) => acc + (curr?.adjustments[0]?.metadata?.freeQty || 0), 0))
        } else if (cartData && !cartData?.items?.length) {
            navigate('/cart')
        }
    }, [cartData])


    useEffect(() => {
        if (cartData && cartData?.id) {
            setIsLoading(true)
            createPaymentSessionsApi(cartData.id).then((cart) => {
                setCartData(cart?.data?.cart)
                setIsLoading(false)
            }).catch((err) => {
                setNotification(err?.response?.data?.message)
                setIsLoading(false)
            })
        }
    }, [])


    function getKeyByValue(object, value) {
        for (const [key, val] of Object.entries(object)) {
            if (val === value) {
                return key;
            }
        }
        return null;
    }

    useEffect(() => {
        if (user) {
            setShippingAddress(user?.customer?.shipping_addresses?.filter((item) => item.metadata.type === 'shipping'))
            setBillingAddress(user?.customer?.shipping_addresses?.filter((item) => item.metadata.type === 'billing'))
        }
    }, [user])


    useEffect(() => {
        if (shippingAddress.length > 0) {
            let primary = shippingAddress?.find(item => item?.metadata?.is_primary)
            if (primary) {
                setSelectShippingAddress(primary)
            }
        }
        if (billingAddress.length > 0) {
            let primary = billingAddress?.find(item => item?.metadata?.is_primary)
            if (primary) {
                setSelectBillingAddress(primary)
            }
        }
    }, [shippingAddress, billingAddress])


    useEffect(() => {
        if (isAddressSame) {
            setSelectBillingAddress(selectShippingAddress)
        }
    }, [isAddressSame, selectShippingAddress])


    const selectPaymentSession = (e, providerId) => {
        setIsLoading(true)
        selectPaymentSessionApi(cartData.id, {
            provider_id: providerId
        }).then((cart) => {
            if (cart?.data?.cart) {
                setPaymentType(e.target.value)
                setPaymentSession(cart.data?.cart)
                setIsLoading(false)
            } else {
                setIsLoading(false)
                setNotification({
                    type: 'error',
                    message: 'Something went wrong, please try again!'
                })
            }
        })
    }



    const onContinue = () => {
        setCardNumberElement(elements.getElement(CardNumberElement));
        const purchaseData = {
            purchaseOrderNumber: purchaseOrderNumber,
            purchaseOrderReference: purchaseOrderReference ?? undefined,
            contract: contract ?? undefined,
        }
        const cardData = {
            cardHolderName: creditCardHolderName,
            purchaseOrderReference: purchaseOrderReference ?? undefined,
            contract: contract ?? undefined
        }
        setIsSubmitting(true)
        updateCartApi(cartData.id, {
            shipping_address: {
                first_name: selectShippingAddress?.first_name,
                last_name: selectShippingAddress?.last_name,
                company: selectShippingAddress?.company,
                address_1: selectShippingAddress?.address_1,
                city: selectShippingAddress?.city,
                province: selectShippingAddress?.province,
                country_code: selectShippingAddress?.country_code,
                postal_code: selectShippingAddress?.postal_code,
                metadata: {
                    department: selectShippingAddress?.metadata?.department,
                    building: selectShippingAddress?.metadata?.building,
                    lab: selectShippingAddress?.metadata?.lab,
                    attention_to: selectShippingAddress?.metadata?.attention_to
                }
            },
            billing_address: {
                first_name: selectBillingAddress?.first_name,
                last_name: selectBillingAddress?.last_name,
                company: selectBillingAddress?.company,
                address_1: selectBillingAddress?.address_1,
                city: selectBillingAddress?.city,
                province: selectBillingAddress?.province,
                country_code: selectBillingAddress?.country_code,
                postal_code: selectBillingAddress?.postal_code,
                metadata: {
                    department: selectBillingAddress?.metadata?.department,
                    building: selectBillingAddress?.metadata?.building,
                    lab: selectBillingAddress?.metadata?.lab,
                    attention_to: selectBillingAddress?.metadata?.attention_to,
                }
            },
            context: paymentType === 'purchase' ? purchaseData : cardData
        }).then((res) => {
            if (res && res.data && res.data?.cart) {
                setStep(2)
                setIsSubmitting(false)
                gtagCheckout(res?.data?.cart)
            } else {
                setIsSubmitting(false)
                setNotification({
                    type: 'red',
                    message: 'Something went wrong, please try again!'
                })
            }
        })

    }

    const completePayment = async () => {
        setIsSubmitting(true)
        if (!stripe || !elements || !cardNumberElement || !cartData?.id) {
            setIsSubmitting(false)
            setNotification({
                type: 'red',
                message: 'Something went wrong, please try again!',
            })
        }

        await stripe
            .confirmCardPayment(
                paymentSession?.payment_session?.data?.client_secret, {
                payment_method: {
                    card: elements.getElement(CardNumberElement) || cardNumberElement,
                    billing_details: {
                        name:
                            creditCardHolderName,
                        address: {
                            city: selectBillingAddress?.city ?? undefined,
                            country: selectBillingAddress?.country_code ?? undefined,
                            line1: selectBillingAddress?.address_1 ?? undefined,
                            line2: selectBillingAddress?.address_2 ?? undefined,
                            postal_code: selectBillingAddress?.postal_code ?? undefined,
                            state: selectBillingAddress?.province ?? undefined,
                        },
                        email: user?.customer.email,
                        phone: user?.customer?.phone ?? undefined,
                    },
                },
            })
            .then(({ error, paymentIntent }) => {
                if (error) {
                    const pi = error.payment_intent

                    if (
                        (pi && pi.status === "requires_capture") ||
                        (pi && pi.status === "succeeded")
                    ) {
                        completeCartApi(cartData.id).then((res) => {
                            if (res.data) {
                                gtagBooking(res?.data?.data)
                                setCompletedOrderData(res.data.data?.display_id)
                                setIsSubmitting(false)
                                setCompleteOrderModal(true)
                                setCartData(null)
                                removeLocalStorage('cartId')
                            } else {
                                setIsSubmitting(false)
                                setNotification({
                                    type: 'red',
                                    message: 'Something went wrong, please try again!'
                                })
                            }
                        })
                    }
                    setIsSubmitting(false)
                    setNotification({
                        type: 'red',
                        message: error.message,
                    })
                    return
                }

                if (
                    (paymentIntent && paymentIntent.status === "requires_capture") ||
                    paymentIntent.status === "succeeded"
                ) {
                    return completeCartApi(cartData.id).then((res) => {
                        if (res.data) {
                            gtagBooking(res?.data?.data)
                            setCompletedOrderData(res?.data?.data?.display_id)
                            setIsSubmitting(false)
                            setCompleteOrderModal(true)
                            setCartData(null)
                            removeLocalStorage('cartId')
                        } else {
                            setIsSubmitting(false)
                            setNotification({
                                type: 'red',
                                message: 'Something went wrong, please try again!'
                            })
                        }
                    })
                }

                return
            })
    }


    const completeManualPayment = () => {
        completeCartApi(cartData.id).then((res) => {
            if (res.data?.data?.display_id) {
                gtagBooking(res?.data?.data)
                setCompletedOrderData(res.data.data?.display_id)
                setIsLoading(false)
                setCompleteOrderModal(true)
                setCartData(null)
                removeLocalStorage('cartId')
            } else {
                setIsLoading(false)
                setNotification({
                    type: 'red',
                    message: 'Something went wrong, please try again!'
                })
            }
        })
    }



    return (
        <Layout>
            <div className={css.wrapper}>
                <h3>Checkout</h3>
                <div className={css.content}>
                    <div className={css.leftContainer}>
                        <div className={css.checkoutContainer}>
                            <div className={css.checkoutHeader}>
                                <span className={`${step === 1 ? css.stepIcon : css.stepIconComplete}`}>
                                    {step === 1 ? 1 : <i className='far fa-check'></i>}
                                </span>
                                <p>Shipping, Billing and Payment</p>
                            </div>
                            {step === 2 && <div className={css.checkoutHeader}>
                                <span className={css.stepIcon}>
                                    2
                                </span>
                                <h4>Review & Place Order</h4>
                            </div>
                            }
                            {/* {step === 1 ?  */}
                            <div className={css.cardsContainer}>
                                {step === 1 ?
                                    <div className={css.card}>
                                        <div className={css.header}>
                                            <p>Select your <span style={{ fontWeight: 700 }}>Shipping Address</span></p>
                                            <div className={css.headerBtnContainer}>
                                                <button className={css.link} onClick={() => {
                                                    setShippingAddressModal(true)
                                                    setSelectedAddress(null)
                                                }}><i className='far fa-plus'></i> Add New</button>
                                            </div>
                                        </div>
                                        <div className={css.addressContainer}>
                                            {shippingAddress.map((item, index) => {
                                                return (
                                                    <div className={`${css.addressCard} ${selectShippingAddress?.id === item.id ? `${css.activeCard}` : ''}`} key={item.id}>
                                                        <div className={css.cardLeftContainer} onClick={() => setSelectShippingAddress(item)}>
                                                            <div className={css.firstContainer}>
                                                                <span className={`${css.selectedCheck} ${selectShippingAddress?.id === item.id ? `${css.selectedCheckActive}` : ''}`}></span>
                                                            </div>
                                                            <div className={css.secondContainer}>
                                                                <h5> {item.company} {item.metadata?.is_primary ? <span>Primary</span> : ''}</h5>
                                                                <p>{item.address_1}, {item.city}, {item.province} {item.postal_code}, {getKeyByValue(countryCode, item?.country_code?.toUpperCase())}</p>
                                                            </div>
                                                        </div>
                                                        <div className={css.cardRightContainer}>
                                                            <button className={css.link}
                                                                onClick={() => {
                                                                    setShippingAddressModal(true)
                                                                    setSelectedAddress(item)
                                                                }}
                                                            >Edit</button>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        <hr />
                                        <div className={css.threeRow}>
                                            <div className={css.moreDetailCard}>
                                                <h5>Building</h5>
                                                <h4>{selectShippingAddress?.metadata?.building}</h4>
                                            </div>
                                            <div className={css.moreDetailCard}>
                                                <h5>Lab or Room Number</h5>
                                                <h4>{selectShippingAddress?.metadata?.lab}</h4>
                                            </div>
                                            <div className={css.moreDetailCard}>
                                                <h5>Attention To</h5>
                                                <h4>{selectShippingAddress?.metadata?.attention_to}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    <div className={css.card}>
                                        <div className={css.header}>
                                            <h4><span style={{ fontWeight: 700 }}>Shipping Address</span></h4>
                                            <div className={css.headerBtnContainer}>
                                                <button className={css.link} onClick={() => {
                                                    setStep(1)
                                                }}>Change</button>
                                            </div>
                                        </div>
                                        <div className={css.addressContainer}>
                                            <div className={css.addressCard}>
                                                <div className={css.cardLeftContainer} style={{ cursor: 'default' }}>
                                                    <div className={css.secondContainer}>
                                                        <h5> {selectShippingAddress.company}</h5>
                                                        <p>{selectShippingAddress.address_1}, {selectShippingAddress.city}, {selectShippingAddress.province} {selectShippingAddress.postal_code}, {getKeyByValue(countryCode, selectShippingAddress?.country_code?.toUpperCase())}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`${css.addressCard} ${css.threeRow}`}>
                                                <div className={css.moreDetail}>
                                                    <h5>Building</h5>
                                                    <h4>{selectShippingAddress.metadata?.building}</h4>
                                                </div>
                                                <div className={css.moreDetail}>
                                                    <h5>Lab or Room Number</h5>
                                                    <h4>{selectShippingAddress.metadata?.lab}</h4>
                                                </div>
                                                <div className={css.moreDetail}>
                                                    <h5>Attention to</h5>
                                                    <h4>{selectShippingAddress?.metadata?.attention_to}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                                {step === 1 ? <div className={css.card}>
                                    <div className={css.header}>
                                        <p>Select your <span style={{ fontWeight: 700 }}>Billing Address</span></p>
                                        <div className={css.headerBtnContainer}>
                                            <button className={css.link} onClick={() => {
                                                setBillingAddressModal(true)
                                                setSelectedAddress(null)
                                            }}><i className='far fa-plus'></i> Add New</button>
                                        </div>
                                    </div>
                                    <div className={css.addressContainer}>
                                        <div className={`${css.addressCard} ${isAddressSame ? `${css.activeCard}` : ''}`}>
                                            <div className={css.cardLeftContainer} onClick={() => {
                                                setIsAddressSame(true)
                                                setSelectBillingAddress(selectShippingAddress)
                                            }} style={{ justifyContent: 'center' }}>
                                                <div className={css.firstContainer}>
                                                    <span className={`${css.selectedCheck} ${isAddressSame ? `${css.selectedCheckActive}` : ''}`}></span>
                                                </div>
                                                <div className={css.secondContainer} style={{ justifyContent: 'center' }}>
                                                    <h5>Same as Shipping</h5>
                                                </div>
                                            </div>
                                        </div>
                                        {billingAddress.map((item, index) => {
                                            return (
                                                <div className={`${css.addressCard} ${!isAddressSame && selectBillingAddress?.id === item.id ? css.activeCard : ''}`} key={item.id}>
                                                    <div className={css.cardLeftContainer} onClick={() => {
                                                        setSelectBillingAddress(item)
                                                        setIsAddressSame(false)
                                                    }}>
                                                        <div className={css.firstContainer}>
                                                            <span className={`${css.selectedCheck} ${!isAddressSame && selectBillingAddress?.id === item.id ? css.selectedCheckActive : ''}`}></span>
                                                        </div>
                                                        <div className={css.secondContainer}>
                                                            <h5> {item.company} {item.metadata?.is_primary ? <span>Primary</span> : ''}</h5>
                                                            <p>{item.address_1}, {item.city}, {item.province} {item.postal_code}, {getKeyByValue(countryCode, item?.country_code?.toUpperCase())}</p>
                                                        </div>
                                                    </div>
                                                    <div className={css.cardRightContainer}>
                                                        <button className={css.link}
                                                            onClick={() => {
                                                                setBillingAddressModal(true)
                                                                setSelectedAddress(item)
                                                            }}
                                                        >Edit</button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <hr />
                                    <div className={css.threeRow}>
                                        <div className={css.moreDetailCard}>
                                            <h5>Building</h5>
                                            <h4>{selectBillingAddress?.metadata?.building}</h4>
                                        </div>
                                        <div className={css.moreDetailCard}>
                                            <h5>Lab or Room Number</h5>
                                            <h4>{selectBillingAddress?.metadata?.lab}</h4>
                                        </div>
                                        <div className={css.moreDetailCard}>
                                            <h5>Attention To</h5>
                                            <h4>{selectBillingAddress?.metadata?.attention_to}</h4>
                                        </div>
                                    </div>
                                </div>
                                    :
                                    <div className={css.card}>
                                        <div className={css.header}>
                                            <p><span style={{ fontWeight: 700 }}>Billing Address</span></p>
                                            <div className={css.headerBtnContainer}>
                                                <button className={css.link} onClick={() => {
                                                    setStep(1)
                                                }}>Change</button>
                                            </div>
                                        </div>
                                        <div className={css.addressContainer}>
                                            <div className={css.addressCard}>
                                                <div className={css.cardLeftContainer} style={{ cursor: 'default' }}>
                                                    <div className={css.secondContainer}>
                                                        {isAddressSame ? <h5>Same as Shipping</h5>
                                                            :
                                                            <>
                                                                <h5> {selectBillingAddress.company}</h5>
                                                                <p>{selectBillingAddress.address_1}, {selectBillingAddress.city}, {selectBillingAddress.province} {selectBillingAddress.postal_code}, {getKeyByValue(countryCode, selectBillingAddress?.country_code?.toUpperCase())}</p>
                                                            </>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {!isAddressSame && <div className={`${css.addressCard} ${css.threeRow}`}>
                                                <div className={css.moreDetail}>
                                                    <h5>Building</h5>
                                                    <h4>{selectBillingAddress.metadata?.building}</h4>
                                                </div>
                                                <div className={css.moreDetail}>
                                                    <h5>Lab or Room Number</h5>
                                                    <h4>{selectBillingAddress.metadata?.lab}</h4>
                                                </div>
                                                <div className={css.moreDetail}>
                                                    <h5>Attention to</h5>
                                                    <h4>{selectBillingAddress?.metadata?.attention_to}</h4>
                                                </div>
                                                <div>
                                                </div>
                                            </div>
                                            }
                                        </div>
                                    </div>
                                }
                                <div className={css.card} style={{ display: step === 1 ? 'block' : 'none', marginBottom: '20px' }}>
                                    <div className={css.header} style={{ marginBottom: '10px' }}>
                                        <p>Select <span style={{ fontWeight: 700 }}>Payment</span></p>
                                    </div>
                                    <div className={css.addressContainer}>
                                        <div className={css.inputContainer}>
                                            <div className={css.selectOption}>
                                                <label class={css.containers}>
                                                    <input type="radio" checked={paymentType === 'purchase'} name="payment" value="purchase" onChange={(e) => {
                                                        // setPaymentType(e.target.value)
                                                        selectPaymentSession(e, 'manual')
                                                    }} />
                                                    <span class={css.checkmark}></span>
                                                    <span>Purchase Order</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div className={css.inputContainer}>
                                            <div className={css.selectOption}>
                                                <label class={css.containers}>
                                                    <input type="radio" checked={paymentType === 'card'} name="payment" value="card" onChange={(e) => {
                                                        // setPaymentType(e.target.value)
                                                        selectPaymentSession(e, 'stripe')
                                                    }} />
                                                    <span class={css.checkmark}></span>
                                                    <span>Credit Card</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={css.paymentContainer} style={{ marginTop: '10px' }}>
                                        {paymentType === 'purchase' ? <>
                                            <Input type='text' className={css.purchase} required label='Enter Purchase Order Number' value={purchaseOrderNumber} onChange={(e) => setPurchaseOrderNumber(e.target.value)} style={{height:'74px'}}/>
                                            <div className={css.twoRow}>
                                                <Input type='text' placeholder={'Purchase Order/Credit Card reference'} value={purchaseOrderReference} onChange={(e) => setPurchaseOrderReference(e.target.value)} style={{height:'74px'}} />
                                                <Input type='text' placeholder={'Contract'} value={contract} onChange={(e) => setContract(e.target.value)} style={{height:'74px'}} />
                                            </div>
                                        </>
                                            :
                                            paymentType === 'card' && <>
                                                <Input type='text' style={{ marginTop: '10px', height:'74px' }} required className={css.creditCard} label='Name on Credit Card' value={creditCardHolderName} onChange={(e) => setCreditCardHolderName(e.target.value)}/>
                                                <CardNumberElement
                                                    className={css.cardNumberElement}
                                                    onChange={(e) => {
                                                        if (e.complete) {
                                                            setCardDetails({
                                                                ...cardDetails,
                                                                cardNumber: true
                                                            })
                                                        }
                                                    }}
                                                    options={{
                                                        required: true,
                                                        showIcon: true,
                                                        iconStyle: "solid",
                                                        placeholder: "4242 4242 4242 4242",
                                                        style: {
                                                            base: {
                                                                fontSize: "16px",
                                                                opacity: 0.5,
                                                                lineHeight: "20px",
                                                                fontWeight: "400",
                                                                color: "#fff",
                                                                "::placeholder": { color: "#fff" },
                                                                "::focus": {border:'1px solid #294156'}
                                                            },
                                                        }
                                                    }}
                                                />
                                                <div className={css.twoRow}>
                                                    <CardExpiryElement
                                                        className={css.cardNumberElement}
                                                        onChange={(e) => {
                                                            if (e.complete) {
                                                                setCardDetails({
                                                                    ...cardDetails,
                                                                    cardExpiry: true
                                                                })
                                                            }
                                                        }}
                                                        options={{
                                                            placeholder: "Expiration Date (MM/YY)*",
                                                            style: {
                                                                base: {
                                                                    fontSize: "16px",
                                                                    opacity: 0.5,
                                                                    lineHeight: "20px",
                                                                    fontWeight: "400",
                                                                    color: "#fff",
                                                                    "::placeholder": { color: "#fff" },

                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <CardCvcElement
                                                        className={css.cardNumberElement}
                                                        onChange={(e) => {
                                                            if (e.complete) {
                                                                setCardDetails({
                                                                    ...cardDetails,
                                                                    cardCvc: true
                                                                })
                                                            }
                                                        }}
                                                        options={{
                                                            placeholder: "CVV*",
                                                            style: {
                                                                base: {
                                                                    fontSize: "16px",
                                                                    opacity: 0.5,
                                                                    lineHeight: "20px",
                                                                    fontWeight: "400",
                                                                    color: "#fff",
                                                                    "::placeholder": { color: "#fff" },
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className={css.twoRow}>
                                                    <Input type='text' placeholder={'Purchase Order/Credit Card reference'} value={purchaseOrderReference} onChange={(e) => setPurchaseOrderReference(e.target.value)} style={{height:'74px'}} />
                                                    <Input type='text' placeholder={'Contract'} value={contract} onChange={(e) => setContract(e.target.value)} style={{height:'74px'}} />
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>
                                {step === 2 && <div className={css.card}>
                                    <div className={css.header}>
                                        <p><span style={{ fontWeight: 700 }}>Payment</span></p>
                                        <div className={css.headerBtnContainer}>
                                            <button className={css.link} onClick={() => {
                                                setStep(1)
                                            }}>Change</button>
                                        </div>
                                    </div>
                                    <div className={css.addressContainer}>
                                        <div className={css.addressCard}>
                                            <div className={css.cardLeftContainer} style={{ cursor: 'default' }}>
                                                <div className={css.secondContainer}>
                                                    <h5>{paymentType === 'purchase' ? 'Purchase Order' : 'Credit Card'}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        {paymentType === 'purchase' && <div className={css.addressCard}>
                                            <div className={css.cardLeftContainer} style={{ cursor: 'default' }}>
                                                <div className={css.secondContainer}>
                                                    <h6 style={{ fontSize: '14px' }}>Purchase Order #</h6>
                                                    <h5>{purchaseOrderNumber}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        }
                                    </div>
                                </div>
                                }
                                {step === 2 &&
                                    <div className={css.card}>
                                        <div className={css.header}>
                                            <p><span style={{ fontWeight: 700 }}>Items</span></p>
                                            <div className={css.headerBtnContainer}>
                                                <button className={css.link} onClick={() => {
                                                    navigate('/cart')
                                                }}>Edit</button>
                                            </div>
                                        </div>
                                        <div className={css.addressContainer}>
                                            {cartData?.items.map((item) => {
                                                return <div className={css.item} key={item.id}>
                                                    <div className={css.itemContent}>
                                                        {item?.mid_code?.search('SA') < 0 ? <h4>{item.variant.product.title}, {item.variant.product.metadata.subclass}</h4>
                                                            : <h4>{item.variant.product.title}</h4>
                                                        }
                                                        <div className={css.itemDetails}>
                                                            <div className={css.details}>
                                                                <h6>Cat ID#</h6>
                                                                <h5>{item.variant.product.mid_code}{item.variant.product.material}</h5>
                                                            </div>
                                                            <div className={css.details}>
                                                                <h6>Concentration</h6>
                                                                <h5>{item.variant.product.concentration}</h5>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={css.quantityContent}>
                                                        <h6>Q-ty:</h6>
                                                        <h5>{item.quantity}</h5>
                                                        {item?.adjustments[0]?.metadata?.freeQty && (
                                                            <h6 className={css.free}>{item.adjustments[0].metadata.freeQty} item{item.adjustments[0].metadata.freeQty > 1 ? 's' : ''} free</h6>
                                                        )}
                                                    </div>
                                                    <div className={css.priceContent}>
                                                        <h4>${(item.subtotal / 100).toFixed(2)}</h4>
                                                       {item.quantity > 1 && <h6>${(item.unit_price / 100).toFixed(2)} x {item.quantity}</h6>}
                                                    </div>
                                                </div>
                                            })}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    <div className={css.rightContainer}>
                        <h4>Summary</h4>
                        <div className={css.priceSummary}>
                            <div className={css.price}>
                                <p>{quantity} Items</p>
                                <p>${cartData ? (cartData?.subtotal / 100)?.toFixed(2) : 0}</p>
                            </div>
                            <div className={css.price}>
                                <p>Tax</p>
                                <p>${cartData ? (cartData?.tax_total / 100)?.toFixed(2) : 0}</p>
                            </div>
                            {freeQuantity > 0 &&
                                <div className={css.price}>
                                    <p>Discount</p>
                                    <p>-${cartData ? (cartData?.discount_total / 100)?.toFixed(2) : 0}</p>
                                </div>
                            }
                            <div className={css.price}>
                                <p>Shipping & Handling</p>
                                <p>${cartData ? (cartData?.shipping_total / 100)?.toFixed(2) : 0}</p>
                            </div>
                            <hr />
                            <div className={css.price}>
                                <p>Grand Total</p>
                                <p style={{ fontWeight: 700 }}>${cartData ? (cartData?.total / 100)?.toFixed(2) : 0}</p>
                            </div>
                        </div>
                        <Checkbox label='I have read and agreed to the' higlighted='Terms and Conditions of Sale'
                            checked={isAgree}
                            onChange={() => setIsAgree(!isAgree)}
                            higlightedLink={() => navigate("/terms-and-conditions-of-sale")}
                        />
                        <button className={`btn ${css.ctaBtn}`}
                            disabled={paymentType === 'card' ? !isAgree || !cardDetails?.cardNumber || !cardDetails?.cardExpiry || !cardDetails?.cardCvc || !creditCardHolderName || isSubmitting : !purchaseOrderNumber || !isAgree || isSubmitting || !selectShippingAddress || !selectBillingAddress}
                            onClick={() => {
                                if (step == 1) {
                                    onContinue()
                                } else {
                                    paymentType === 'purchase' ?
                                        completeManualPayment()
                                        : completePayment()
                                    // setCompleteOrderModal(true)
                                }
                            }}
                        >{step === 1 ? 'Continue & Review' : 'Submit'}</button>
                        {step === 2 && <button className={css.link}
                            disabled={isSubmitting}
                            onClick={() => {
                                setStep(1)
                            }}
                        >Back to Address & Payment</button>}
                    </div>
                </div>
            </div>
        </Layout>
    )
}